import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Wand2, Loader2, Coins, Volume2, RotateCcw } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

interface MediaGenerationToolbarProps {
  onGenerateImage: () => void;
  onGenerateAudio: () => void;
  isGeneratingImage: boolean;
  isGeneratingAudio: boolean;
  userCredits: number;
  requiredCredits: number;
  hasImage: boolean;
  hasAudio: boolean;
  disableAudio?: boolean;
  t: (key: string) => string;
  // New props for story rewrite functionality
  isLastPage?: boolean;
  storyId?: string;
  contentId?: string;
  rewritesAvailable?: number;
  isRewriting?: boolean;
  onRewritten?: (updatedRewritesAvailable: number) => void;
  onNavigatePrev?: () => void;
  isFirstPage?: boolean; // New prop to indicate if we're on the first page
  onNavigateToSetup?: () => void; // New prop to handle navigation to setup page
}

const MediaGenerationToolbar: React.FC<MediaGenerationToolbarProps> = ({
  onGenerateImage,
  onGenerateAudio,
  isGeneratingImage,
  isGeneratingAudio,
  userCredits,
  requiredCredits,
  hasImage,
  hasAudio,
  disableAudio = false,
  t,
  // New rewrite props with defaults
  isLastPage = false,
  storyId,
  contentId,
  rewritesAvailable = 0,
  isRewriting = false,
  onRewritten,
  onNavigatePrev,
  isFirstPage = false, // Default to false
  onNavigateToSetup = () => {}, // Default to empty function
}) => {
  // Add state to track the most current rewrites_available value
  const [currentRewritesAvailable, setCurrentRewritesAvailable] = useState(rewritesAvailable);

  // Fetch the current rewrites_available value directly from the database
  useEffect(() => {
    // Only fetch when we have a storyId and we're on the last page where rewrite button shows
    if (storyId && isLastPage) {
      const fetchCurrentRewritesCount = async () => {
        try {
          const { data, error } = await supabase
            .from("stories")
            .select("rewrites_available")
            .eq("id", storyId)
            .single();
            
          if (error) throw error;
          
          if (data && data.rewrites_available !== undefined) {
            setCurrentRewritesAvailable(data.rewrites_available);
          }
        } catch (err) {
          logger.error("Error fetching current rewrites count:", err);
        }
      };
      
      fetchCurrentRewritesCount();
    } else {
      // If not on last page or no storyId, just use the prop value
      setCurrentRewritesAvailable(rewritesAvailable);
    }
  }, [storyId, isLastPage, rewritesAvailable]);

  // Fix the handleRewriteStory function to handle first page case
  const handleRewriteStory = async () => {
    if (!storyId || !contentId || currentRewritesAvailable <= 0 || isRewriting) {
      return;
    }
  
    try {
      // 1. Tell the parent component we're rewriting
      if (onRewritten) {
        logger.log("Setting rewriting state...");
      }
      
      // 2. Delete the current story content
      const { error: deleteError } = await supabase
        .from("story_content")
        .delete()
        .eq("id", contentId);
        
      if (deleteError) throw deleteError;
      
      // 3. Decrease the rewrites_available count
      const { error: updateError } = await supabase
        .from("stories")
        .update({ rewrites_available: currentRewritesAvailable - 1 })
        .eq("id", storyId);
        
      if (updateError) throw updateError;
      
      // Store the updated rewrites count for the UI
      const updatedRewritesAvailable = currentRewritesAvailable - 1;
      
      // Update local state immediately
      setCurrentRewritesAvailable(updatedRewritesAvailable);
  
      // Check if we're on the first page
      if (isFirstPage) {
        logger.log("Rewriting first page - redirecting to setup");
        
        // Notify parent with updated rewrites count BEFORE navigation
        if (onRewritten) {
          onRewritten(updatedRewritesAvailable);
        }
        
        // Navigate to the setup page instead of previous content
        onNavigateToSetup();
        return;
      }
      
      // 4. Set user_input to NULL for the previous page
      const { data: prevContent } = await supabase
        .from("story_content")
        .select("id")
        .eq("story_id", storyId)
        .order("created_at", { ascending: false })
        .limit(1);
  
      if (prevContent && prevContent.length > 0) {
        const { error: resetError } = await supabase
          .from("story_content")
          .update({ user_input: null })
          .eq("id", prevContent[0].id);
          
        if (resetError) throw resetError;
      }
      
      // 5. IMPORTANT: Call onRewritten BEFORE navigation to ensure UI updates
      if (onRewritten) {
        logger.log("Calling onRewritten callback with new value:", updatedRewritesAvailable);
        onRewritten(updatedRewritesAvailable);
      }
      
      // 6. Then navigate to the previous page
      setTimeout(() => {
        if (onNavigatePrev) {
          onNavigatePrev();
        }
      }, 100);
      
    } catch (error) {
      logger.error("Error rewriting story:", error);
    }
  };

  return (
    <div className="flex flex-row items-center gap-2 mb-2">
      {/* Image generation button */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline" 
              size="sm"
              className="flex items-center gap-1"
              onClick={onGenerateImage}
              disabled={isGeneratingImage || userCredits < requiredCredits}
            >
              {isGeneratingImage ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Wand2 className="h-4 w-4" />
              )}
              {requiredCredits}
              <Coins className="h-4 w-4 text-amber-500 mr-1" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {userCredits >= requiredCredits 
              ? `${t('imageGeneration.generateImage')+": "+t('dashboard.author.creditsAvailable').replace('{{count}}', (userCredits || 0).toString())}`
              : `${t('imageGeneration.insufficientCredits')} (${userCredits}/${requiredCredits})`}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      
      {/* Audio generation button */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline" 
              size="sm"
              className="flex items-center gap-1"
              onClick={onGenerateAudio}
              disabled={isGeneratingAudio || userCredits < requiredCredits || hasAudio || disableAudio}
            >
              {isGeneratingAudio ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Volume2 className="h-4 w-4" />
              )}
              {requiredCredits}
              <Coins className="h-4 w-4 text-amber-500 mr-1" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {hasAudio ? 
              t('audioGeneration.audioAvailable') :
              userCredits >= requiredCredits 
                ? `${t('audioGeneration.generateAudio')+": "+t('dashboard.author.creditsAvailable').replace('{{count}}', (userCredits || 0).toString())}`
                : `${t('audioGeneration.insufficientCredits')} (${userCredits}/${requiredCredits})`}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      
      {/* Rewrite story button - only shown on the last page */}
      {isLastPage && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline" 
                size="sm"
                className="flex items-center gap-1"
                onClick={handleRewriteStory}
                disabled={isRewriting || currentRewritesAvailable <= 0}
              >
                {isRewriting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RotateCcw className="h-4 w-4" />
                )}
                {currentRewritesAvailable}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {currentRewritesAvailable > 0 
                ? `${t('storyRewrite.rewriteStory')}: ${currentRewritesAvailable} ${t('storyRewrite.rewritesLeft')}`
                : t('storyRewrite.noRewritesLeft')}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};

export default MediaGenerationToolbar;
